import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { settings as settingsApi } from '../../lib/api';
import api from '../../lib/api';
import { Card, CardHeader, CardBody } from '../../components/ui/Card';
import { Input, Select } from '../../components/ui/Input';
import { Toggle } from '../../components/ui/Toggle';
import { Button } from '../../components/ui/Button';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
const TABS = [
    { id: 'locale', label: 'Location & currency' },
    { id: 'loyalty', label: 'Loyalty programme' },
    { id: 'receipt', label: 'Receipt template' },
    { id: 'features', label: 'Features' },
];
export default function Settings() {
    const [tab, setTab] = useState('locale');
    return (_jsxs("div", { className: "p-6 max-w-3xl space-y-5", children: [_jsx("h1", { className: "text-xl font-semibold text-gray-900", children: "Settings" }), _jsx("div", { className: "flex gap-1 bg-gray-100 rounded-xl p-1 w-fit", children: TABS.map(t => (_jsx("button", { onClick: () => setTab(t.id), className: `px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === t.id
                        ? 'bg-white shadow-sm text-gray-900'
                        : 'text-gray-500 hover:text-gray-700'}`, children: t.label }, t.id))) }), tab === 'locale' && _jsx(LocaleSettings, {}), tab === 'loyalty' && _jsx(LoyaltySettings, {}), tab === 'receipt' && _jsx(ReceiptSettings, {}), tab === 'features' && _jsx(FeatureSettings, {})] }));
}
function LocaleSettings() {
    const { data: countries = [] } = useQuery({
        queryKey: ['countries'],
        queryFn: () => api.get('/locale/countries').then(r => r.data),
    });
    const { data: currencies = [] } = useQuery({
        queryKey: ['currencies'],
        queryFn: () => api.get('/locale/currencies').then(r => r.data),
    });
    const { data: timezones = [] } = useQuery({
        queryKey: ['timezones'],
        queryFn: () => api.get('/locale/timezones').then(r => r.data).catch(() => []),
    });
    const { data: localeData } = useQuery({
        queryKey: ['locale-settings'],
        queryFn: () => settingsApi.locale().then(r => r.data),
    });
    const [form, setForm] = useState({
        country: 'AU', currency_code: 'AUD',
        timezone: 'Australia/Sydney', tax_label: 'GST', tax_rate: '10',
    });
    useEffect(() => {
        if (localeData) {
            setForm({
                country: localeData.country ?? 'AU',
                currency_code: localeData.currency_code ?? 'AUD',
                timezone: localeData.timezone ?? 'Australia/Sydney',
                tax_label: localeData.tax_label ?? 'GST',
                tax_rate: String(localeData.tax_rate ?? '10'),
            });
        }
    }, [localeData]);
    const save = useMutation({
        mutationFn: () => settingsApi.saveLocale({
            ...form,
            tax_rate: parseFloat(form.tax_rate),
        }),
        onSuccess: () => toast.success('Location settings saved'),
        onError: () => toast.error('Failed to save'),
    });
    const f = (key) => ({
        value: form[key],
        onChange: (e) => setForm(p => ({ ...p, [key]: e.target.value })),
    });
    const countryOpts = countries.map((c) => ({ value: c.code, label: `${c.flag ?? ''} ${c.name}` }));
    const currencyOpts = currencies.map((c) => ({ value: c.code, label: `${c.symbol} ${c.code} — ${c.name}` }));
    const timezoneOpts = timezones.map((t) => ({ value: t.value ?? t, label: t.label ?? t }));
    return (_jsxs(Card, { children: [_jsx(CardHeader, { children: _jsx("h3", { className: "text-sm font-medium", children: "Location & currency" }) }), _jsxs(CardBody, { className: "space-y-4", children: [_jsx(SettingRow, { label: "Country", children: _jsx(Select, { options: countryOpts, ...f('country') }) }), _jsx(SettingRow, { label: "Currency", children: _jsx(Select, { options: currencyOpts, ...f('currency_code') }) }), timezoneOpts.length > 0 && (_jsx(SettingRow, { label: "Timezone", children: _jsx(Select, { options: timezoneOpts, ...f('timezone') }) })), _jsx(SettingRow, { label: "Tax label", hint: "Shown on receipts (e.g. GST, VAT, Tax)", children: _jsx(Input, { placeholder: "GST", ...f('tax_label') }) }), _jsx(SettingRow, { label: "Tax rate (%)", hint: "Used for tax breakdown on receipts", children: _jsx(Input, { type: "number", min: "0", max: "100", step: "0.5", ...f('tax_rate') }) }), _jsx("div", { className: "pt-2 border-t border-gray-100", children: _jsx(Button, { onClick: () => save.mutate(), loading: save.isPending, children: "Save settings" }) })] })] }));
}
function LoyaltySettings() {
    const { data } = useQuery({
        queryKey: ['loyalty-settings'],
        queryFn: () => settingsApi.loyalty().then(r => r.data),
    });
    const [form, setForm] = useState({
        enabled: true,
        programme_name: 'Loyalty Points',
        earn_rate: '1',
        redeem_rate: '100',
        min_redeem: '100',
        expiry_months: '12',
    });
    useEffect(() => {
        if (data) {
            setForm(f => ({
                ...f,
                enabled: data.enabled ?? true,
                programme_name: data.programme_name ?? 'Loyalty Points',
                earn_rate: String(data.earn_rate ?? '1'),
                redeem_rate: String(data.redeem_rate ?? '100'),
                min_redeem: String(data.min_redeem ?? '100'),
                expiry_months: String(data.expiry_months ?? '12'),
            }));
        }
    }, [data]);
    const save = useMutation({
        mutationFn: () => settingsApi.saveLoyalty({
            ...form,
            earn_rate: parseFloat(form.earn_rate),
            redeem_rate: parseFloat(form.redeem_rate),
            min_redeem: parseInt(form.min_redeem),
            expiry_months: parseInt(form.expiry_months),
        }),
        onSuccess: () => toast.success('Loyalty settings saved'),
        onError: () => toast.error('Failed to save'),
    });
    const f = (key) => ({
        value: form[key],
        onChange: (e) => setForm(p => ({ ...p, [key]: e.target.value })),
    });
    const earnValue = parseFloat(form.earn_rate || '1');
    const redeemValue = parseFloat(form.redeem_rate || '100');
    return (_jsxs(Card, { children: [_jsx(CardHeader, { children: _jsx("h3", { className: "text-sm font-medium", children: "Loyalty programme" }) }), _jsxs(CardBody, { className: "space-y-4", children: [_jsx(Toggle, { checked: form.enabled, onChange: v => setForm(p => ({ ...p, enabled: v })), label: "Programme active", description: "When off, no points are earned or redeemed at checkout" }), _jsx(SettingRow, { label: "Programme name", children: _jsx(Input, { placeholder: "Loyalty Points", ...f('programme_name') }) }), _jsx(SettingRow, { label: "Earn rate", hint: `${earnValue} point${earnValue !== 1 ? 's' : ''} per $1 spent`, children: _jsx(Input, { type: "number", min: "0.01", step: "0.01", ...f('earn_rate') }) }), _jsx(SettingRow, { label: "Redeem rate", hint: `${redeemValue} points = $1 discount`, children: _jsx(Input, { type: "number", min: "1", ...f('redeem_rate') }) }), _jsx(SettingRow, { label: "Minimum redeem", hint: "Minimum points needed to redeem", children: _jsx(Input, { type: "number", min: "0", ...f('min_redeem') }) }), _jsx(SettingRow, { label: "Points expiry", hint: "Months before unused points expire (0 = never)", children: _jsx(Input, { type: "number", min: "0", ...f('expiry_months') }) }), _jsxs("div", { className: "bg-amber-50 border border-amber-100 rounded-xl p-4 text-sm space-y-1", children: [_jsx("p", { className: "text-xs font-medium text-amber-800 uppercase tracking-wide mb-2", children: "Preview" }), _jsxs("p", { className: "text-amber-700", children: ["$50 purchase \u2192 ", _jsxs("strong", { children: [Math.floor(50 * earnValue), " points"] })] }), _jsxs("p", { className: "text-amber-700", children: [redeemValue, " points \u2192 ", _jsx("strong", { children: "$1.00 off" })] })] }), _jsx("div", { className: "pt-2 border-t border-gray-100", children: _jsx(Button, { onClick: () => save.mutate(), loading: save.isPending, children: "Save loyalty settings" }) })] })] }));
}
function ReceiptSettings() {
    return (_jsxs(Card, { children: [_jsx(CardHeader, { children: _jsx("h3", { className: "text-sm font-medium", children: "Receipt template" }) }), _jsxs(CardBody, { children: [_jsx("p", { className: "text-sm text-gray-500 mb-4", children: "Customise your receipt layout \u2014 logo, header/footer text, QR codes, social links, and paper size." }), _jsx(Link, { to: "/settings/receipt-builder", className: "inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-sm font-medium transition-colors", children: "Open receipt builder \u2192" })] })] }));
}
function FeatureSettings() {
    const qc = useQueryClient();
    const { data } = useQuery({
        queryKey: ['features'],
        queryFn: () => settingsApi.features().then(r => r.data).catch(() => ({})),
    });
    const [features, setFeatures] = useState({});
    useEffect(() => {
        if (data) {
            const parsed = {};
            for (const [k, v] of Object.entries(data)) {
                parsed[k] = v === '1' || v === true;
            }
            setFeatures(parsed);
        }
    }, [data]);
    const save = useMutation({
        mutationFn: () => settingsApi.saveFeatures(features),
        onSuccess: () => {
            toast.success('Feature settings saved');
            qc.invalidateQueries({ queryKey: ['features'] });
        },
        onError: () => toast.error('Failed to save'),
    });
    const FEATURES = [
        { key: 'loyalty_programme', label: 'Loyalty programme', plan: 'Starter', desc: 'Customers earn and redeem points' },
        { key: 'gift_cards', label: 'Gift cards', plan: 'Starter', desc: 'Issue and accept gift cards at checkout' },
        { key: 'credit_accounts', label: 'Credit accounts', plan: 'Starter', desc: 'Customers purchase on credit, pay later' },
        { key: 'barcode_scanner', label: 'Barcode scanner', plan: 'Starter', desc: 'USB/Bluetooth scanner support in POS' },
        { key: 'postpaid_invoicing', label: 'Post-paid invoicing', plan: 'Pro', desc: 'Net 7/14/30 payment terms for wholesale' },
        { key: 'auto_combo', label: 'Auto combo deals', plan: 'Pro', desc: 'Automatic bundle discounts at checkout' },
        { key: 'customer_type_discounts', label: 'Customer type discounts', plan: 'Pro', desc: 'VIP, wholesale, staff discount tiers' },
        { key: 'labor_management', label: 'Labour management', plan: 'Pro', desc: 'Clock in/out, scheduling, payroll export' },
        { key: 'mobile_alerts', label: 'Mobile alerts', plan: 'Pro', desc: 'Push/email alerts for low stock, high sales' },
        { key: 'kitchen_display', label: 'Kitchen display (KDS)', plan: 'Pro', desc: 'Orders routed to kitchen screen' },
        { key: 'weighing_scale', label: 'Weighing scale', plan: 'Pro', desc: 'Per-gram pricing for deli/fresh produce' },
        { key: 'online_ordering', label: 'Online ordering', plan: 'Enterprise', desc: 'Customer-facing order-ahead and delivery' },
        { key: 'multi_location', label: 'Multi-location', plan: 'Pro', desc: 'Manage multiple stores from one account' },
        { key: 'ai_assistant', label: 'AI assistant', plan: 'Pro', desc: 'Claude-powered insights, forecasting, fraud detection' },
    ];
    const planColor = {
        Starter: 'bg-emerald-50 text-emerald-700',
        Pro: 'bg-blue-50 text-blue-700',
        Enterprise: 'bg-purple-50 text-purple-700',
    };
    return (_jsxs(Card, { children: [_jsx(CardHeader, { children: _jsx("h3", { className: "text-sm font-medium", children: "Feature flags" }) }), _jsxs(CardBody, { className: "space-y-1", children: [FEATURES.map(f => (_jsxs("div", { className: "flex items-center gap-4 py-3 border-b border-gray-50 last:border-0", children: [_jsxs("div", { className: "flex-1 min-w-0", children: [_jsx("p", { className: "text-sm font-medium text-gray-800", children: f.label }), _jsx("p", { className: "text-xs text-gray-400 mt-0.5", children: f.desc })] }), _jsx("span", { className: `text-xs font-medium px-2 py-0.5 rounded-full shrink-0 ${planColor[f.plan]}`, children: f.plan }), _jsx(Toggle, { checked: features[f.key] ?? false, onChange: v => setFeatures(p => ({ ...p, [f.key]: v })) })] }, f.key))), _jsx("div", { className: "pt-4", children: _jsx(Button, { onClick: () => save.mutate(), loading: save.isPending, children: "Save features" }) })] })] }));
}
function SettingRow({ label, hint, children }) {
    return (_jsxs("div", { className: "grid grid-cols-5 gap-4 items-start", children: [_jsxs("div", { className: "col-span-2", children: [_jsx("p", { className: "text-sm font-medium text-gray-700", children: label }), hint && _jsx("p", { className: "text-xs text-gray-400 mt-0.5", children: hint })] }), _jsx("div", { className: "col-span-3", children: children })] }));
}
